import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Card,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Grid,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  GifBox as GifIcon,
  AutoAwesome as GenerateIcon
} from '@mui/icons-material';
import GifGenerator from '../components/GifGenerator';
import { Toaster, toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useCredits } from '../hooks/useCredits';
import { eventService } from '../services/eventService';
import UpgradeOverlay from '../components/common/UpgradeOverlay';

export default function GifCreatorPage() {
  const gifGeneratorRef = useRef();
  const previewRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatusMessage, setDownloadStatusMessage] = useState('');
  const navigate = useNavigate();
  const { credits, refetchCredits } = useCredits();
  const [showUpgradeOverlay, setShowUpgradeOverlay] = useState(false);

  // Add state for GIF settings with useCallback
  const [gifState, setGifState] = useState({
    gifText: '<p>Create amazing animated GIFs with Instation</p>',
    gifName: 'Instation',
    gifSize: 'square',
    selectedTheme: 'deep-purple',
    selectedStyle: 'gradient-linear',
    customBgImage: null,
    selectedAnimation: 'gradient',
    contentType: 'text',
    gifImage: null
  });

  useEffect(() => {
    if (credits === 0) {
      setShowUpgradeOverlay(true);
    }
  }, [credits]);

  const handleRealProgress = (progress) => {
    setDownloadProgress(progress);
    if (progress < 100) {
      setDownloadStatusMessage('Encoding GIF...');
    } else {
      setDownloadStatusMessage('Finalizing GIF...');
    }
  };

  const handleDownload = async () => {
    if (credits === 0) {
      setShowUpgradeOverlay(true);
      toast.error('You have no credits left to download GIFs.');
      return;
    }
    if (gifGeneratorRef.current) {
      setIsDownloading(true);
      setDownloadProgress(0);
      setDownloadStatusMessage('Preparing download...');
      try {
        await gifGeneratorRef.current.handleDownload(handleRealProgress);
        
        setDownloadStatusMessage('Download complete!'); 
        toast.success('GIF downloaded successfully!');
        eventService.emit('CREDITS_UPDATED');
        
      } catch (error) {
        console.error("Download failed:", error);
        toast.error('Download failed. Please try again.');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const handleSaveToPost = async () => {
    if (credits === 0) {
      setShowUpgradeOverlay(true);
      toast.error('You have no credits left to save GIFs.');
      return;
    }
    if (gifGeneratorRef.current) {
      setIsDownloading(true); // Use isDownloading for save as well for visual feedback
      setDownloadProgress(0);
      setDownloadStatusMessage('Preparing to save GIF...');
      try {
        await gifGeneratorRef.current.handleSaveToPost(handleRealProgress);
        
        setDownloadStatusMessage('GIF saved successfully!');
        toast.success('GIF saved to your posts successfully!');
        eventService.emit('CREDITS_UPDATED');
        
      } catch (error) {
        toast.error('Failed to save GIF. Please try again.');
        setDownloadStatusMessage('Failed to save GIF.');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  // Handle state changes from the control panel with debounce
  const handleStateChange = useCallback((newState) => {
    console.log('State updated:', newState);
    setGifState(prevState => {
      // Only update if values actually changed
      const hasChanges = Object.entries(newState).some(
        ([key, value]) => prevState[key] !== value
      );
      
      return hasChanges ? newState : prevState;
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>Create Animated GIFs | Instation</title>
        <meta name="description" content="Create beautiful animated GIFs for your social media content with Instation's GIF Creator tool." />
      </Helmet>
      <Toaster position="top-center" />
      <Container maxWidth="xl" className="page-container">
        <Box className="content-container">
          {showUpgradeOverlay && (
            <UpgradeOverlay 
              message="You've used all your credits for this month" 
              buttonText="Upgrade Now" 
              onUpgrade={() => navigate('/dashboard/upgrade')} 
            />
          )}
          <Grid container spacing={3} className="content-grid">
            {/* Left Panel - Controls */}
            <Grid item xs={12} md={5} lg={4} className="content-panel">
              <Paper 
                elevation={0} 
                className="content-paper"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    color: '#2D3748',
                    fontSize: '1.1rem',
                    fontFamily: 'var(--font-family)',
                    flexShrink: 0
                  }}
                >
                  GIF Settings
                </Typography>

                <Box sx={{
                  flex: 1, 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 2,
                  minHeight: 0
                }}>
                  <Box sx={{ p: 2 }}> 
                    <GifGenerator
                      ref={gifGeneratorRef}
                      showPreview={false}
                      onSaveToPost={(blob) => {
                        console.log('Saving GIF to posts:', blob);
                      }}
                      onStateChange={handleStateChange}
                      {...gifState}
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Right Panel - Preview */}
            <Grid item xs={12} md={7} lg={8} className="content-panel">
              <Paper 
                elevation={0} 
                className="content-paper"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                  pb: 2,
                  mb: 3,
                  flexShrink: 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#2D3748',
                        fontSize: '1.1rem',
                        fontFamily: 'var(--font-family)'
                      }}
                    >
                      Preview
                    </Typography>
                    {isDownloading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                        <CircularProgress variant="determinate" value={downloadProgress} size={24} />
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          {downloadStatusMessage} ({downloadProgress}%)
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ 
                    display: 'flex',
                    gap: 1,
                    mt: 0,
                    justifyContent: 'flex-end'
                  }}>
                    <Tooltip title="Download GIF (1 credit)">
                      <IconButton
                        onClick={handleDownload}
                        disabled={isDownloading}
                        sx={{ color: '#4890fe' }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Save to post (1 credit)">
                      <IconButton
                        onClick={handleSaveToPost}
                        disabled={isDownloading}
                        sx={{ color: '#28a745' }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box
                  ref={previewRef}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    p: 0,
                    position: 'relative',
                    overflow: 'auto',
                    minHeight: 0,
                  }}
                >
                  <Box sx={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: 2
                  }}>
                    <GifGenerator
                      previewOnly={true}
                      showPreview={true}
                      {...gifState}
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
} 